import { ValidatorFn } from '@angular/forms';
import { Subject } from 'rxjs';
import { RolUsuario } from 'src/app/compartido/autorizacion/autorizacion.models';

export interface Formulario {
  config: ConfiguracionFormulario | null;
  valor: ValorFormulario;
  valido: boolean;
}

export interface ConfiguracionFormulario {
  campos: CampoFormulario[];
  estilo?: EstiloFormulario;
  debounce?: number; // Tiempo en ms para debounce general
}

export interface EstiloFormulario {
  orientacion?: OrientacionFormulario;
  display?: DisplayFormulario;
  posicionLabel?: PosicionLabelFormulario;
  widthLabel?: string; // Ej: '150px', '20%'
  widthCampo?: string; // Ej: '300px', '50%'
  esconderErrores?: boolean;
  marcarRequeridos?: boolean;
  marcarOpcionales?: boolean;
  labelNegrilla?: boolean;
}

export enum OrientacionFormulario {
  HORIZONTAL = 'horizontal', // Por defecto
  VERTICAL = 'vertical',
}

export enum DisplayFormulario {
  FLEX = 'flex', // Por defecto
  GRID = 'grid',
}

export enum PosicionLabelFormulario {
  DENTRO = 'dentro', // Por defecto
  TOP = 'top',
  IZQUIERDA = 'izquierda',
  NONE = 'none',
}

interface BaseCampoFormulario<T> {
  nombre: string;
  tipo: TipoCampoFormulario;
  valor?: T | null;
  slug: string;
  requerido?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  info?: string; // Información adicional para mostrar en un tooltip
  validadores?: ValidadorFormulario[];
  oculto?: boolean;
  roles?: RolUsuario[];
  valorOut?: Subject<any>; // Para leer el valor
  valorIn?: Subject<any>; // Para cambiar el valor
  requeridoIn?: Subject<boolean>; // Para cambiar si es requerido
  debounce?: number; // Tiempo en ms para debounce individual
}

export type CampoFormulario =
  | CampoTexto
  | CampoNumero
  | CampoFecha
  | CampoArchivo
  | CampoOpcionMultiple<string | number>
  | CampoAutocomplete<string | number>
  | CampoBooleano
  | CampoCoordenadas;

export interface CampoTexto extends BaseCampoFormulario<string> {
  tipo: TipoCampoFormulario.TEXTO;
  contrasena?: boolean;
  correo?: boolean;
  url?: boolean;
  longitudMinima?: number;
  longitudMaxima?: number;
  patron?: string | RegExp;
  multilinea?: boolean; // Si se renderiza como textarea en vez de input
  filas?: number; // Alto del textarea cuando multilinea es true (por defecto 4)
}

export interface CampoNumero extends BaseCampoFormulario<number> {
  tipo: TipoCampoFormulario.NUMERO;
  minimo?: number;
  maximo?: number;
  prefijo?: string;
  sufijo?: string;
  telefono?: boolean;
  longitudMinima?: number;
  longitudMaxima?: number;
  decimal?: boolean;
  separadorMiles?: boolean;
}

export interface CampoFecha extends BaseCampoFormulario<string> {
  tipo: TipoCampoFormulario.FECHA;
  minimo?: string;
  maximo?: string;
}

export interface CampoArchivo extends BaseCampoFormulario<Blob> {
  tipo: TipoCampoFormulario.ARCHIVO;
  /* Usar '*\/*' para aceptar cualquier tipo de archivo */
  aceptados: (
    | '.csv'
    | '.xlsx'
    | '.xlsb'
    | '.ods'
    | '.txt'
    | '.pdf'
    | '.png'
    | '.jpg'
    | '.jpeg'
    | '.zip'
    | '*/*'
  )[];
  multiples?: boolean;
  escaner?: ('camara' | 'carga' | 'imagenes')[];
}

export interface CampoOpcionMultiple<T> extends BaseCampoFormulario<T | T[]> {
  tipo: TipoCampoFormulario.OPCION_MULTIPLE;
  multiple?: boolean; // Si permite seleccionar múltiples opciones
  buscador?: boolean; // Si tiene un campo para buscar entre las opciones
  buscadorOut?: Subject<string>; // Cuando se escribe en el buscador
  opciones: OpcionCampo<T>[];
  opcionesIn?: Subject<OpcionCampo<T>[]>; // Para cambiar las opciones
  opcionOut?: Subject<OpcionCampo<T>>; // Cuando se selecciona una opción
}

export interface CampoAutocomplete<T> extends BaseCampoFormulario<T> {
  tipo: TipoCampoFormulario.AUTOCOMPLETE;
  soloOpcion?: boolean; // Si el valor tiene que ser una de las opciones
  filtrarOpciones?: boolean; // Si se deben filtrar las opciones al escribir
  opciones: OpcionCampo<T>[];
  opcionesIn?: Subject<OpcionCampo<T>[]>; // Para cambiar las opciones
  opcionOut?: Subject<OpcionCampo<T>>; // Cuando se selecciona una opción
}

export interface CampoBooleano extends BaseCampoFormulario<boolean> {
  tipo: TipoCampoFormulario.BOOLEANO;
}

export interface CampoCoordenadas extends BaseCampoFormulario<{
  latitud: number;
  longitud: number;
}> {
  tipo: TipoCampoFormulario.COORDENADAS;
  height?: string; // Altura del mapa, ej: '400px'
  zoom?: number; // Zoom inicial del mapa
  centro?: { lat: number; lng: number }; // Centro inicial del mapa
}

export interface OpcionCampo<T> {
  nombre: string;
  valor: T;
  extra?: any;
}

export enum TipoCampoFormulario {
  TEXTO = 'texto',
  NUMERO = 'numero',
  FECHA = 'fecha',
  ARCHIVO = 'archivo',
  OPCION_MULTIPLE = 'opción-multiple',
  AUTOCOMPLETE = 'autocomplete',
  BOOLEANO = 'booleano',
  COORDENADAS = 'coordenadas',
}

/* Validador de un campo del formulario */
export type ValidadorFormulario = {
  mensajeError: string;
  validador: ValidatorFn;
};

/* Valores formulario */
export interface ValorFormulario {
  [slug: string]: any;
}

/* Cambio en formulario */
export interface CambioFormulario {
  valor: ValorFormulario;
  valido: boolean;
}
